// MapEditorPortal.jsx — Opens MapEditorModal when a map embed requests edit.
// Every embed opens the SAME report-wide shared Y.XmlFragment (see mapFragmentInit.js) — there
// is one shared map per report, and this portal is just the "editor session" for whichever
// embed most recently asked to edit it.
//
// Sync model:
//   Local writes: editorState changes → debounced prosemirrorToYXmlFragment (outbound)
//   Remote reads: fragment.observe → mapDocFromFragment → replaceWith tr (inbound)
//
// The inbound observer is guarded by `remoteOriginRef` so that the resulting
// React state update does not immediately echo back through the outbound write.

import React, { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { prosemirrorToYXmlFragment } from 'y-prosemirror';
import { subscribeMapEditorOpen, buildMapEditorRequestFromDoc } from './MapSession';
import { getSharedMapFragment } from './mapFragmentInit';
import { mapStateFromFragment } from './editor/mapCollaborationPlugins';
import { ensureDefaultMapLayer, extractMapState, mapDocFromFragment } from './schema/mapPmAdapter';
import { DEFAULT_VIEW_ID } from './schema/mapSchema';
import { roundCoord } from './schema/geoCoords';
import useYDocStore from '../../stores/useYDocStore';
import { useReportStore } from '../../features/products/useReportStore';
import { useSession } from '../../firebase/UserProvider';
import EditorPortalShell from '../editor-shell/EditorPortalShell';

const MapEditorModal = lazy(() => import('./editor/MapEditorModal'));

const FRAGMENT_WRITE_DEBOUNCE_MS = 120;

// map-editor-url-state ticket 05: debounce window for mirroring the live DTG scrub position into
// the `mapDtg` URL param (see `handleDtgRangeChange` below) — long enough to coalesce a drag
// gesture's rapid-fire onChange stream into one write per pause, short enough that a deliberate
// click-jump or arrow-key step still shows up promptly.
const MAP_DTG_URL_DEBOUNCE_MS = 300;

// map-editor-url-state ticket 01: the search param that mirrors "is the map editor open, and
// for which embed" — presence = open, value = that map node's `nodeId`. Kept exactly in step
// with `session?.nodeId` below (interactive open pushes it, close/cold-open-resolution replace
// it) so a pasted/back-navigated URL reopens the same embed the original link pointed at.
const MAP_NODE_PARAM = 'mapNode';

// map-editor-url-state ticket 02/03: `mapView`/`mapDtg` mirror the session's saved-view
// selection and DTG scrub position — always alongside `mapNode`, never without it (see
// `applyMapParams`/`clearMapParams` below, which set/clear all three together).
//
// map-editor-url-state ticket 05: `mapDtg`'s READ direction is still open-time-only (resolved
// once, from a URL override if present+valid, else the node's own persisted attr, then left
// alone even if the URL is hand-edited again while the editor stays open — no reactive re-scrub
// path, deliberately, to avoid a write/read feedback loop with the live WRITE below). Its WRITE
// direction, however, DOES now live-update — see `handleDtgRangeChange`'s debounced mirror,
// which is separate from (and doesn't touch) the un-debounced `dtgRangeRef`/close-time node
// commit that already existed. `mapView` similarly live-updates on the write side — see
// `handleWindowSettingsChange` below; ticket 03 wired `viewId` into the same live "window
// setting" write-back path basemap/grid/overlay/title already used, since selecting a view in
// the left panel is exactly that same kind of live, immediate edit. Both are omitted from the
// URL when they'd just be the "nothing special" default (see `applyMapParams`/`viewParamValue`).
const MAP_VIEW_PARAM = 'mapView';
const MAP_DTG_PARAM = 'mapDtg';

// map-editor-url-state ticket 04/06: `mapBbox` mirrors the live crop-frame position. While a
// named view is selected, it's only set while diverged (beyond tolerance — see useMapViews.js's
// `bboxesMatch`) from that view's own stored bbox; `null`/absent once they match again. While no
// view is selected ("All markings"), it unconditionally mirrors the current camera position on
// every settle instead (no baseline to diverge from), except the session's very first settle,
// which is skipped so a fresh no-view open starts clean (see useMapViews.js's
// `hasHadFirstSettleRef`). Unlike `mapView`/`mapDtg`, it's never persisted onto the node — URL-
// only/ephemeral, per the batch-grill-me decision. It IS two-way like the others: a valid
// `mapBbox` present on open lands the crop frame there instead of the resolved view's own bbox
// (see `openSession`'s `bboxOverride` option) — applies regardless of whether the resolved view
// is a named one or the default "All markings" (ticket 06 lifted the earlier named-view-only
// restriction).
const MAP_BBOX_PARAM = 'mapBbox';

// The "All markings" default sentinel (and a null/unset viewId) aren't worth cluttering the URL
// with — shared by `applyMapParams`' open/close-time write and `handleWindowSettingsChange`'s
// live one so the omission rule can't drift between the two.
function viewParamValue(viewId) {
    return viewId && viewId !== DEFAULT_VIEW_ID ? viewId : null;
}

// Nested `[[w,s],[e,n]]` lon/lat bbox -> `"w,s,e,n"` URL param value (rounded to the same 5-
// decimal/~1m precision `geoCoords.js`'s `roundCoord` uses elsewhere), or `null` for anything
// that isn't 4 finite numbers.
function encodeBboxParam(bbox) {
    if (!bbox) return null;
    const [[w, s], [e, n]] = bbox;
    const nums = [w, s, e, n];
    if (!nums.every(Number.isFinite)) return null;
    return nums.map(roundCoord).join(',');
}

// Inverse of `encodeBboxParam` — malformed/non-numeric input (stale link, hand-edited URL)
// silently becomes `null` rather than throwing, matching this feature area's existing "drop
// invalid overrides" posture for `mapView`/`mapDtg`.
function decodeBboxParam(raw) {
    if (!raw) return null;
    const nums = raw.split(',').map(Number);
    if (nums.length !== 4 || !nums.every(Number.isFinite)) return null;
    const [w, s, e, n] = nums;
    return [[w, s], [e, n]];
}

// map-editor-url-state ticket 02: builds the full three-param set for a just-opened session in
// one shot — `viewId`/`dtgSelectedMs` already reflect whatever `openSession` resolved (URL
// override merged with the node's persisted value, or just the node's persisted value for an
// interactive open), so this only has to decide what's worth showing in the URL at all.
function applyMapParams(prev, {nodeId, viewId, dtgSelectedMs, bbox}) {
    const next = new URLSearchParams(prev);
    next.set(MAP_NODE_PARAM, nodeId);
    const view = viewParamValue(viewId);
    if (view) next.set(MAP_VIEW_PARAM, view);
    else next.delete(MAP_VIEW_PARAM);
    if (Number.isFinite(dtgSelectedMs)) next.set(MAP_DTG_PARAM, String(dtgSelectedMs));
    else next.delete(MAP_DTG_PARAM);
    const encodedBbox = encodeBboxParam(bbox);
    if (encodedBbox) next.set(MAP_BBOX_PARAM, encodedBbox);
    else next.delete(MAP_BBOX_PARAM);
    // Cold-open's "normalize the URL" pass (see the URL → open effect below) calls this on every
    // successful resolution, including the common case where the URL already matched exactly —
    // skip the no-op `replace` navigation rather than churning history for nothing.
    return next.toString() === prev.toString() ? prev : next;
}

function clearMapParams(prev) {
    if (![MAP_NODE_PARAM, MAP_VIEW_PARAM, MAP_DTG_PARAM, MAP_BBOX_PARAM].some((k) => prev.has(k))) return prev;
    const next = new URLSearchParams(prev);
    next.delete(MAP_NODE_PARAM);
    next.delete(MAP_VIEW_PARAM);
    next.delete(MAP_DTG_PARAM);
    next.delete(MAP_BBOX_PARAM);
    return next;
}

const MapEditorPortal = ({isReadOnly = false}) => {
    const [session, setSession] = useState(null);
    const [editorState, setEditorState] = useState(null);
    const ydoc = useYDocStore((s) => s.ydoc);
    const provider = useYDocStore((s) => s.provider);
    const editorView = useReportStore((s) => s.editorView);
    const { user: currentUser } = useSession() || {};
    const [searchParams, setSearchParams] = useSearchParams();

    const fragmentRef = useRef(null);
    const writeTimerRef = useRef(null);
    const editorStateRef = useRef(null);
    // Mirrors `session?.nodeId` — the cold-open effect below reads this synchronously to decide
    // whether an incoming `mapNode` param already matches the currently-open session, so it
    // never needs to depend on `session` itself (and re-run every render `session` changes).
    const openNodeIdRef = useRef(null);
    // True while a remote Yjs change is being applied as a PM transaction so
    // the outbound debounced write can skip the echo.
    const remoteOriginRef = useRef(false);
    // Ticket 03: latest live dtgSelectedMs value from MapEditorModal (kept in a ref, not React
    // state — mirrors editorStateRef/remoteOriginRef above), updated on every scrub change via
    // the modal's onDtgRangeChange prop and read exactly once, at close time, so the flush-back
    // happens once per close rather than per-drag/keystroke.
    // shared-map-data ticket 02: only carries `valueMs` now — the `[startMs, endMs]` range lives
    // on the shared map doc's `map_meta` node and is written live via the normal editorState →
    // Yjs sync below, same path layers/markings/views already use.
    const dtgRangeRef = useRef(null);
    // map-editor-url-state ticket 05: debounce timer for mirroring the live scrub position into
    // the `mapDtg` URL param (see `handleDtgRangeChange` below) — separate from `dtgRangeRef`
    // above, which stays an un-debounced, always-current ref (the close-time node commit must
    // never miss the final value, even if closed before the debounce below would have fired).
    const dtgUrlDebounceRef = useRef(null);

    const ydocRef = useRef(ydoc);
    useEffect(() => { ydocRef.current = ydoc; }, [ydoc]);

    const providerRef = useRef(provider);
    useEffect(() => { providerRef.current = provider; }, [provider]);

    useEffect(() => {
        editorStateRef.current = editorState;
    }, [editorState]);

    const flushFragmentWrite = useCallback(() => {
        const state = editorStateRef.current;
        const fragment = fragmentRef.current;
        if (!state || !fragment || !ydocRef.current) return;
        ydocRef.current.transact(() => {
            prosemirrorToYXmlFragment(state.doc, fragment);
        }, 'map-editor');
    }, []);

    const handleClose = useCallback(() => {
        if (writeTimerRef.current) {
            clearTimeout(writeTimerRef.current);
            writeTimerRef.current = null;
        }
        if (dtgUrlDebounceRef.current) {
            clearTimeout(dtgUrlDebounceRef.current);
            dtgUrlDebounceRef.current = null;
        }
        flushFragmentWrite();
        // Ticket 03: one-shot write-back of the timeline's final selected point onto the
        // originating node's attrs, via the callback MapNodeView handed us in the open request.
        // Reads whatever onDtgRangeChange last recorded — if the modal never reported a change
        // (e.g. closed immediately), this is still the seeded initial value, so the write is a
        // no-op in practice but harmless.
        const commit = session?.onDtgRangeCommit;
        if (commit && dtgRangeRef.current) {
            commit(dtgRangeRef.current);
        }
        dtgRangeRef.current = null;
        openNodeIdRef.current = null;
        setSession(null);
        setEditorState(null);
        fragmentRef.current = null;
        editorStateRef.current = null;
        // map-editor-url-state ticket 01: always `replace` on close — this mirrors the modal's
        // state closing, it's not itself a navigation the user should be able to "undo" with
        // Back (that's what the pushed entry from the interactive-open path is for; see the
        // open-subscription effect below).
        setSearchParams(clearMapParams, { replace: true });
    }, [flushFragmentWrite, session, setSearchParams]);

    // Viewport-capture pre-close flush: MapEditorModal calls this with the synchronously-
    // computed updated PM state (view bbox added/updated) just before calling handleClose.
    // We write that state directly to the Yjs fragment here, bypassing the React-state async
    // path (editorStateRef.current would otherwise lag behind by one render cycle and miss
    // the new view node in the regular flushFragmentWrite call above).
    const handleBeforeClose = useCallback((updatedState) => {
        const fragment = fragmentRef.current;
        if (!updatedState || !fragment || !ydocRef.current) return;
        ydocRef.current.transact(() => {
            prosemirrorToYXmlFragment(updatedState.doc, fragment);
        }, 'map-editor');
        // Keep editorStateRef in sync so the subsequent flushFragmentWrite in handleClose
        // doesn't overwrite this with an older doc (it reads the same ref).
        editorStateRef.current = updatedState;
    }, []);

    // Ticket 03: records the modal's live dtgRange on every change so handleClose can read the
    // final value without the modal (or MapSession) needing any other way to reach back out to
    // the portal — mirrors how editorStateRef mirrors editorState for the fragment-flush path.
    //
    // map-editor-url-state ticket 05: also mirrors the scrub position into the `mapDtg` URL
    // param, debounced ~300ms after the last change — this fires on drag, click-jump, AND
    // arrow-key stepping alike (MapTimeline.jsx routes all three through the same
    // `range.setValue`/`stepDay` → `onDtgRangeChange` path; see useDtgRange.js), so one timer
    // here covers every way the scrub position can move without MapTimeline needing to
    // distinguish "gesture end" itself. `{replace: true}` — like the `viewId`/`mapBbox` mirrors,
    // this isn't a navigation Back should step through one scrub-tick at a time. Deliberately
    // does NOT touch `dtgRangeRef`/the node's persisted attr — that stays close-only-commit,
    // exactly as before this ticket; this is purely an additional URL-only mirror of the same
    // already-live value.
    const handleDtgRangeChange = useCallback((dtg) => {
        dtgRangeRef.current = dtg;
        if (dtgUrlDebounceRef.current) clearTimeout(dtgUrlDebounceRef.current);
        dtgUrlDebounceRef.current = setTimeout(() => {
            dtgUrlDebounceRef.current = null;
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (Number.isFinite(dtg?.valueMs)) next.set(MAP_DTG_PARAM, String(dtg.valueMs));
                else next.delete(MAP_DTG_PARAM);
                return next.toString() === prev.toString() ? prev : next;
            }, { replace: true });
        }, MAP_DTG_URL_DEBOUNCE_MS);
    }, [setSearchParams]);

    // shared-map-data ticket 03: unlike dtgRangeRef above, window-settings changes (basemap/
    // grid/overlay/title/viewId) forward straight through to the session's own
    // `onWindowSettingsChange` (MapNodeView's live `setNodeMarkup` write-back) on every change —
    // no ref-batched close-time flush needed since there's no shared-doc round-trip involved,
    // unlike `dtgStartMs`/`dtgEndMs` above.
    //
    // map-editor-url-state ticket 03: also mirrors a `viewId` patch into the `mapView` URL param
    // — `{replace: true}` since, unlike the mapNode-open push, this isn't a navigation the user
    // would expect Back to step through one view-switch at a time (Back should still just close
    // the editor directly). No equivalent exists for the other window-setting keys (basemap/
    // grid/overlay/title) — only `viewId` doubles as a URL param.
    const handleWindowSettingsChange = useCallback((patch) => {
        session?.onWindowSettingsChange?.(patch);
        if (!Object.prototype.hasOwnProperty.call(patch, 'viewId')) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const view = viewParamValue(patch.viewId);
            if (view) next.set(MAP_VIEW_PARAM, view);
            else next.delete(MAP_VIEW_PARAM);
            return next.toString() === prev.toString() ? prev : next;
        }, { replace: true });
    }, [session, setSearchParams]);

    // map-editor-url-state ticket 04/06: mirrors MapEditorModal's live camera-position tracking
    // (via `onBboxChange`, sourced from useMapViews' `divergedBbox`) into the `mapBbox` URL param
    // — `null` clears it. Deliberately doesn't touch `session.onWindowSettingsChange`/the node's
    // attrs at all, unlike the `viewId` mirror above: per the batch-grill-me decision, this is
    // URL-only/ephemeral, never persisted, whether it's tracking divergence from a named view or
    // (ticket 06) just the raw camera position while no view is selected. `{replace: true}` for
    // the same reason as the `viewId` mirror — not a Back-steppable navigation.
    const handleBboxChange = useCallback((bbox) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const encoded = encodeBboxParam(bbox);
            if (encoded) next.set(MAP_BBOX_PARAM, encoded);
            else next.delete(MAP_BBOX_PARAM);
            return next.toString() === prev.toString() ? prev : next;
        }, { replace: true });
    }, [setSearchParams]);

    // ── Session construction, shared by interactive open and URL-driven cold-open ──────────────
    // map-editor-url-state ticket 01: factored out of the open-subscription effect below so the
    // cold-open effect (which builds an equivalent request via `buildMapEditorRequestFromDoc`
    // instead of MapNodeView's `_openEditor()`) can reuse the exact same session-construction
    // logic rather than duplicating it.
    //
    // map-editor-url-state ticket 02: `viewIdOverride` lets the cold-open effect thread a
    // candidate `mapView` URL value through — validated here (against the just-loaded map doc's
    // actual `map_view` nodes) rather than by the caller, since the shared map fragment/doc is
    // only available once this function has fetched it. An invalid/unknown override (stale link,
    // deleted view) is silently dropped in favor of `viewId` (the node's own persisted value) —
    // there's deliberately no equivalent override for `dtgSelectedMs`; the caller resolves that
    // one itself since it's a plain numeric validity check with no doc to consult.
    //
    // Returns the resolved `{nodeId, viewId, dtgSelectedMs, bbox}` actually applied (so callers
    // can reflect exactly that into the URL, e.g. dropping a rejected override) or `null` if no
    // session was opened.
    const openSession = useCallback(({
        nodeId, viewId, dtgSelectedMs, title, basemapStyleId, activeGridId, overlayType,
        onDtgRangeCommit, onWindowSettingsChange,
    }, {viewIdOverride, bboxOverride} = {}) => {
        if (!nodeId || !ydocRef.current) return null;

        const fragment = getSharedMapFragment(ydocRef.current);
        fragmentRef.current = fragment;
        const awareness = providerRef.current?.awareness ?? null;
        const nextEditorState = ensureDefaultMapLayer(mapStateFromFragment(fragment, awareness));
        setEditorState(nextEditorState);

        let resolvedViewId = viewId ?? null;
        if (viewIdOverride) {
            const {views} = extractMapState(nextEditorState.doc);
            const overrideIsValid = viewIdOverride === DEFAULT_VIEW_ID
                || views.some((v) => v.viewId === viewIdOverride);
            if (overrideIsValid) resolvedViewId = viewIdOverride;
        }
        // map-editor-url-state ticket 04/06: `mapBbox` applies regardless of whether the
        // resolved view is a named one or the default "All markings" — ticket 06 lifted the
        // earlier named-view-only restriction, since a no-view deep link is just as meaningful a
        // "start the camera here" request as a named-view one.
        const resolvedBbox = bboxOverride ?? null;

        // Ticket 03: seed dtgRangeRef with the node's persisted dtgSelectedMs up front, so a
        // close-with-no-change still writes back exactly what was opened with (idempotent)
        // rather than leaving the ref stale from a previous session.
        dtgRangeRef.current = { valueMs: dtgSelectedMs ?? null };
        openNodeIdRef.current = nodeId;
        setSession({
            nodeId,
            viewId: resolvedViewId,
            dtgSelectedMs: dtgSelectedMs ?? null,
            bbox: resolvedBbox,
            title,
            basemapStyleId,
            activeGridId,
            overlayType,
            onDtgRangeCommit: onDtgRangeCommit ?? null,
            onWindowSettingsChange: onWindowSettingsChange ?? null,
        });
        return { nodeId, viewId: resolvedViewId, dtgSelectedMs: dtgSelectedMs ?? null, bbox: resolvedBbox };
    }, []);

    // ── Open subscription (interactive open, e.g. double-click on the embed) ───────────────────
    useEffect(() => {
        return subscribeMapEditorOpen((request) => {
            const opened = openSession(request);
            if (!opened) return;
            // map-editor-url-state ticket 01: push (not replace) — an interactive open always
            // has a well-defined "closed" state already on the history stack immediately before
            // it (the report page as the user was just looking at it), so the Back button
            // closing the modal can never navigate past the report itself.
            setSearchParams((prev) => applyMapParams(prev, opened), { replace: false });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSession]);

    // ── URL → open (deep link / page load / browser back-forward) ──────────────────────────────
    // map-editor-url-state ticket 01: reactive on every `mapNode` param change (not just mount),
    // so an in-app link/navigation to `?mapNode=<id>` while the report page is already open also
    // opens it. Always `replace`s (never pushes) when resolving — the URL already carries this
    // state by the time this effect runs, whether from the initial page load or an interactive
    // open's own push above (which this effect also sees, but no-ops on via the
    // `openNodeIdRef` check below).
    useEffect(() => {
        const nodeId = searchParams.get(MAP_NODE_PARAM);
        if (!nodeId || nodeId === openNodeIdRef.current) return;
        // Report doc/Yjs doc not loaded yet — wait for both to become available (this effect
        // re-runs once they do, since both are reactive state, not refs) rather than stripping a
        // perfectly valid param prematurely.
        if (!editorView || !ydoc) return;

        const request = buildMapEditorRequestFromDoc(editorView, nodeId);
        if (!request) {
            // Stale id (deleted node, wrong report) — silently strip rather than error, per
            // map-editor-url-state ticket 01.
            setSearchParams(clearMapParams, { replace: true });
            return;
        }

        // map-editor-url-state ticket 02: a `mapDtg` override is just a numeric validity check
        // (no doc to consult), so it's resolved right here — an invalid/missing value falls back
        // to the node's own persisted `dtgSelectedMs` from `request`. `mapView`'s override is
        // validated inside `openSession` instead (it needs the just-loaded map doc's actual
        // views, which only `openSession` fetches).
        const rawDtg = searchParams.get(MAP_DTG_PARAM);
        const dtgOverride = rawDtg ? Number(rawDtg) : NaN;
        const dtgSelectedMs = Number.isFinite(dtgOverride) ? dtgOverride : request.dtgSelectedMs;

        // map-editor-url-state ticket 04/06: like `mapDtg`, a `mapBbox` override is a plain shape/
        // numeric-validity decode (no doc to consult) — `openSession` now applies it regardless
        // of whether the resolved view ends up named or the default "All markings" (ticket 06).
        const bboxOverride = decodeBboxParam(searchParams.get(MAP_BBOX_PARAM));

        const opened = openSession(
            { ...request, dtgSelectedMs },
            { viewIdOverride: searchParams.get(MAP_VIEW_PARAM), bboxOverride },
        );
        if (!opened) return;
        // Normalizes the URL to whatever actually got applied — e.g. drops a rejected/stale
        // `mapView` override, or a `mapDtg` that failed the finite-number check above.
        setSearchParams((prev) => applyMapParams(prev, opened), { replace: true });
    }, [searchParams, editorView, ydoc, openSession, setSearchParams]);

    // ── URL → close (browser Back/Forward popping the pushed-on-open entry) ───────────────────
    // map-editor-url-state ticket 01: an interactive open pushes `mapNode=<id>` (see the
    // open-subscription effect above) specifically so Back closes the modal — but Back only
    // changes the URL, it doesn't call `handleClose` itself. `openNodeIdRef` (rather than
    // `session`) tells this apart from every OTHER way the param can go missing that must NOT
    // re-trigger a close: it's already `null` by the time `handleClose`'s own `setSearchParams`
    // call lands (title-bar close, or this effect's own prior run), and it's never set at all
    // while a stale/unresolvable deep-link param is being stripped (the cold-open effect above
    // only sets it once `openSession` actually succeeds).
    //
    // Deliberately depends on `[searchParams]` ONLY (via `handleCloseRef`, not `handleClose`
    // itself) — `handleClose`'s identity churns every time `session` changes (it closes over
    // `session` for the one-shot dtg-commit read), including the very moment a session is
    // OPENED. Depending on `handleClose` directly would re-run this effect right then, while
    // `searchParams` itself hasn't yet caught up to the just-pushed `mapNode` param (the two are
    // separate state updates, batched into the same commit but the location update trails by one
    // more re-render) — misreading that transient gap as "the URL went missing" and closing the
    // session immediately after opening it.
    const handleCloseRef = useRef(handleClose);
    useEffect(() => { handleCloseRef.current = handleClose; }, [handleClose]);
    useEffect(() => {
        if (!searchParams.get(MAP_NODE_PARAM) && openNodeIdRef.current) {
            handleCloseRef.current();
        }
    }, [searchParams]);

    // ── Inbound: remote Yjs changes → PM EditorState ────────────────────────
    // fragment.observe fires whenever a remote peer (or another tab) writes to
    // this map's Y.XmlFragment.  We rebuild the PM doc from the fragment and
    // apply a non-history transaction so undo is not polluted.
    useEffect(() => {
        const fragment = fragmentRef.current;
        if (!fragment || !session) return;

        const handler = (events, tr) => {
            // Skip changes that originated from this editor's own outbound write.
            if (tr?.origin === 'map-editor') return;
            const nextDoc = mapDocFromFragment(fragment);
            if (!nextDoc) return;
            setEditorState((prev) => {
                if (!prev) return prev;
                const pmTr = prev.tr.replaceWith(0, prev.doc.content.size, nextDoc.content);
                pmTr.setMeta('addToHistory', false);
                remoteOriginRef.current = true;
                return prev.apply(pmTr);
            });
        };

        fragment.observeDeep(handler);
        return () => fragment.unobserveDeep(handler);
    }, [session]);

    // ── Outbound: PM EditorState → Yjs fragment (debounced) ─────────────────
    // Skip the write when the state change originated from an inbound remote
    // update to avoid an echo that would create a redundant Yjs transaction.
    useEffect(() => {
        if (!editorState || !fragmentRef.current || !ydocRef.current) return;

        if (remoteOriginRef.current) {
            remoteOriginRef.current = false;
            return;
        }

        if (writeTimerRef.current) {
            clearTimeout(writeTimerRef.current);
        }

        writeTimerRef.current = setTimeout(() => {
            const fragment = fragmentRef.current;
            const state = editorStateRef.current;
            if (!fragment || !state || !ydocRef.current) return;
            ydocRef.current.transact(() => {
                prosemirrorToYXmlFragment(state.doc, fragment);
            }, 'map-editor');
        }, FRAGMENT_WRITE_DEBOUNCE_MS);

        return () => {
            if (writeTimerRef.current) {
                clearTimeout(writeTimerRef.current);
                writeTimerRef.current = null;
            }
        };
    }, [editorState]);

    return (
        <EditorPortalShell open={!!session} onClose={handleClose} ariaLabel="Map editor">
            {session && editorState && (
                <MapEditorModal
                    viewId={session.viewId ?? null}
                    dtgSelectedMs={session.dtgSelectedMs ?? null}
                    onDtgRangeChange={handleDtgRangeChange}
                    bbox={session.bbox ?? null}
                    onBboxChange={handleBboxChange}
                    title={session.title}
                    basemapStyleId={session.basemapStyleId}
                    activeGridId={session.activeGridId}
                    overlayType={session.overlayType}
                    onWindowSettingsChange={handleWindowSettingsChange}
                    editorState={editorState}
                    setEditorState={setEditorState}
                    onClose={handleClose}
                    onBeforeClose={handleBeforeClose}
                    currentUser={currentUser ?? null}
                    remoteChangeRef={remoteOriginRef}
                    isReadOnly={isReadOnly}
                />
            )}
        </EditorPortalShell>
    );
};

export default MapEditorPortal;
